import { useQueryClient } from '@tanstack/react-query'
import React, { lazy, type ReactElement, Suspense, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Loader } from '../components/Loader'
import { Layout } from '../components/Shared/Layout'

const LazySignInPage = lazy(() =>
  import('../pages/signin/SignInPage').then((module) => ({ default: module.SignInPage })),
)
import { PasskeyRegistrationPrompt } from '../components/Passkey/PasskeyRegistrationPrompt'
import { getFeatureSwitch } from '../services/api/featureSwitches'
import { exchangeToken, signInPassword, ssoInit, updatePlatformHeader } from '../services/api/auth'
import { clearSavedTokens, getRefreshToken, saveTokens } from '../services/authentication'
import { setPlatformForLogs } from '../services/datadog'
import { isWebAuthnSupported, isPlatformAuthenticatorAvailable, hasRegisteredPasskey, syncStoredCredentialIds } from '../services/webauthn'
import type { PasswordSignInOptions, SsoSignInOptions } from '../types/SignInOptions'
import { redirectToUrl } from '../utils/location'
import { QueryParameters } from '../utils/queryParameters'

export enum DevParams {
  CLEAR_AUTH_COOKIES = 'clear-auth-cookies',
}

export type Platform = string

export const BaseSignInRoute = (
  redirectCallback: (
    accessToken: string,
    authenticationCode: string | undefined,
    secondaryFactor: string | undefined,
    redirectTo: string | undefined,
  ) => Promise<void>,
  platform: Platform,
): ReactElement => {
  const [searchParams] = useSearchParams()
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false)
  const pendingRedirect = useRef<(() => Promise<void>) | null>(null)
  const queryClient = useQueryClient()

  updatePlatformHeader(platform)
  setPlatformForLogs(platform)

  const performRedirect = async (accessToken: string): Promise<void> => {
    const authenticationCode = searchParams.get(QueryParameters.AUTHENTICATION_CODE) || undefined
    const secondaryFactor = searchParams.get(QueryParameters.SECONDARY_FACTOR) || undefined
    const redirectTo = searchParams.get(QueryParameters.REDIRECT_TO) || undefined
    await redirectCallback(accessToken, authenticationCode, secondaryFactor, redirectTo)
  }

  const signInSubmit = async (signInOptions: SsoSignInOptions | PasswordSignInOptions): Promise<void> => {
    if (signInOptions.isSso) {
      const successRedirectTo = searchParams.get(QueryParameters.REDIRECT_TO)
      const redirectUrl = new URL(window.location.href.split('?')[0])
      if (successRedirectTo) {
        redirectUrl.searchParams.set(QueryParameters.REDIRECT_TO, successRedirectTo)
      }
      const redirectUri = redirectUrl.toString()
      ssoInit(signInOptions.username, redirectUri)
    } else {
      const { username, password, authenticationCode, secondaryFactor } = signInOptions
      const tokens = await signInPassword(username, password, authenticationCode, secondaryFactor)
      saveTokens(tokens.access_token, tokens.refresh_token)

      try {
        await syncStoredCredentialIds()
      } catch (error) {
        console.warn('Failed to sync stored credential IDs:', error)
      }

      const alreadyHasPasskey = hasRegisteredPasskey()
      // Resolved fresh (not read from render-time hook state) so a fast submit
      // (autofill + Enter) can't race the flag's first fetch and silently skip
      // the prompt while the switch is actually enabled.
      const passkeyEnabled = await queryClient
        .ensureQueryData({
          queryKey: ['feature-switch', 'passkey'],
          queryFn: () => getFeatureSwitch('passkey'),
          staleTime: 30_000,
        })
        .catch(() => false)
      const shouldPrompt = passkeyEnabled
        && !alreadyHasPasskey
        && isWebAuthnSupported()
        && await isPlatformAuthenticatorAvailable()

      if (shouldPrompt) {
        pendingRedirect.current = () => performRedirect(tokens.access_token)
        setShowPasskeyPrompt(true)
      } else {
        await performRedirect(tokens.access_token)
      }
    }
  }

  const saveTokensAndRedirect = async (accessToken: string, refreshToken: string | null): Promise<void> => {
    saveTokens(accessToken, refreshToken)
    await performRedirect(accessToken)
  }

  const getUsernameFromQueryParams = () => {
    const usernameParam = searchParams.get(QueryParameters.USERNAME) || undefined
    // Remove the param from the url
    const url = new URL(window.location.href)
    const params = new URLSearchParams(url.search)
    params.delete(QueryParameters.USERNAME)
    const newUrl = url.pathname + (params.toString() ? `?${params.toString()}` : '')
    window.history.replaceState({}, '', newUrl)
    return usernameParam
  }
  if (searchParams.get(DevParams.CLEAR_AUTH_COOKIES)?.toLowerCase() === 'true') {
    const url = new URL(window.location.href)
    const searchParams = new URLSearchParams(url.search)
    searchParams.delete(DevParams.CLEAR_AUTH_COOKIES)
    const newUrl = `${url.origin}${url.pathname}?${searchParams.toString()}`
    clearSavedTokens()
    redirectToUrl(newUrl)
  }

  const usernameParam = getUsernameFromQueryParams()

  if (showPasskeyPrompt) {
    const handleComplete = () => pendingRedirect.current?.()
    return (
      <Layout>
        <PasskeyRegistrationPrompt onComplete={handleComplete} onSkip={handleComplete} />
      </Layout>
    )
  }

  // redirect with token from query parameters
  const accessTokenParam = searchParams.get(QueryParameters.ACCESS_TOKEN)

  // User uses email / password
  // After authentication the access_token and refresh_token are passed as searchParams.
  // They need to be saved in a cookie
  if (accessTokenParam) {
    const refreshTokenParam = searchParams.get(QueryParameters.REFRESH_TOKEN)
    // Ignoring the error as we are trying with the refresh token next
    saveTokensAndRedirect(accessTokenParam, refreshTokenParam)
    return <Loader />
  }

  // User uses cookie
  // The user was already authenticated, the token is read from a cookie
  const refreshTokenCookie = getRefreshToken()
  if (refreshTokenCookie) {
    exchangeToken(refreshTokenCookie)
      .then((tokens) => saveTokensAndRedirect(tokens.access_token, tokens.refresh_token))
      .catch((error) => {
        // failure to exchange a refresh token mostly happens when a Swiler switches environment
        console.error(error)
        clearSavedTokens()
        redirectToUrl(window.location.href)
      })
    return <Loader />
  }

  return (
    <Suspense fallback={<Loader />}>
      <LazySignInPage
        onSubmit={signInSubmit}
        enableSso={true}
        platform={platform}
        username={usernameParam}
      />
    </Suspense>
  )
}
